import { EQUIPMENT_RPC, type EquipmentAsset, type EquipmentProtocol, type StaffEquipmentItem, type StaffEquipmentType } from "@/types/equipment";
import { EquipmentApiError } from "@/lib/equipmentMessages";
import { toEquipmentAsset, toEquipmentProtocol } from "@/lib/equipmentQueries";
import {
  rpcEquipment,
  uploadProtocolPhotos,
  validateConditionNotes,
  validateProtocolPhotos,
} from "@/lib/equipmentStorage";

export { EquipmentApiError, equipmentErrorMessagePl } from "@/lib/equipmentMessages";
export {
  createEquipmentAsset,
  listEquipmentAssets,
  listEquipmentProtocols,
  listStaffEquipment,
  updateEquipmentAsset,
} from "@/lib/equipmentQueries";
export {
  resolveEquipmentPhotoUrl,
  uploadProtocolPhotos,
  validateConditionNotes,
  validateProtocolPhotos,
} from "@/lib/equipmentStorage";

const ACTIVE_STAFF_STATUSES = ["pending_handover", "assigned", "pending_return"] as const;

export function isActiveStaffEquipment(item: Pick<StaffEquipmentItem, "status">): boolean {
  return (ACTIVE_STAFF_STATUSES as readonly string[]).includes(item.status);
}

export function canHandoverAsset(asset: Pick<EquipmentAsset, "status">): boolean {
  return asset.status === "available";
}

export function canReturnAsset(asset: Pick<EquipmentAsset, "status">): boolean {
  return asset.status === "assigned";
}

export function canReturnStaffItem(item: Pick<StaffEquipmentItem, "status">): boolean {
  return item.status === "assigned";
}

export function canRespondToProtocol(
  protocol: Pick<EquipmentProtocol, "status" | "direction" | "worker_id" | "initiated_by">,
  userId: string,
  isManagement: boolean,
): boolean {
  if (protocol.status !== "pending" || userId === protocol.initiated_by) return false;
  if (protocol.direction === "handover") return userId === protocol.worker_id;
  return userId === protocol.worker_id || isManagement;
}

export function canCancelProtocol(
  protocol: Pick<EquipmentProtocol, "status" | "initiated_by">,
  userId: string,
  isManagement: boolean,
): boolean {
  return protocol.status === "pending" && (userId === protocol.initiated_by || isManagement);
}

function requireOrgAndWorker(orgId: string, workerId: string): void {
  if (!orgId) throw new EquipmentApiError("EQUIPMENT_ORG_REQUIRED");
  if (!workerId) throw new EquipmentApiError("EQUIPMENT_WORKER_REQUIRED");
}

async function attachEvidence(
  protocol: EquipmentProtocol,
  files: File[] | undefined,
  notes: string | null,
): Promise<EquipmentProtocol> {
  if (!files?.length) return protocol;
  const photoUrls = await uploadProtocolPhotos(protocol.org_id, protocol.id, files);
  return toEquipmentProtocol(
    await rpcEquipment(
      EQUIPMENT_RPC.setEvidence,
      {
        p_protocol_id: protocol.id,
        p_condition_notes: notes,
        p_photo_urls: photoUrls,
      },
      "setEvidence",
    ),
  );
}

export async function initiateAssetHandover(input: {
  orgId: string;
  workerId: string;
  asset: Pick<EquipmentAsset, "id" | "status">;
  conditionNotes?: string | null;
  photos?: File[];
}): Promise<EquipmentProtocol> {
  requireOrgAndWorker(input.orgId, input.workerId);
  if (input.asset.status === "retired") throw new EquipmentApiError("EQUIPMENT_RETIRED");
  if (!canHandoverAsset(input.asset)) throw new EquipmentApiError("EQUIPMENT_NOT_AVAILABLE");
  validateProtocolPhotos(input.photos ?? []);
  const notes = validateConditionNotes(input.conditionNotes);

  const protocol = toEquipmentProtocol(
    await rpcEquipment(
      EQUIPMENT_RPC.initiateHandover,
      {
        p_org_id: input.orgId,
        p_worker_id: input.workerId,
        p_kind: "asset",
        p_asset_id: input.asset.id,
        p_condition_notes: notes,
      },
      "initiateAssetHandover",
    ),
  );
  return attachEvidence(protocol, input.photos, notes);
}

export async function initiateKeyCardHandover(input: {
  orgId: string;
  workerId: string;
  name: string;
  type?: StaffEquipmentType;
  conditionNotes?: string | null;
  photos?: File[];
}): Promise<EquipmentProtocol> {
  requireOrgAndWorker(input.orgId, input.workerId);
  const name = input.name.trim();
  if (!name) throw new EquipmentApiError("EQUIPMENT_NAME_REQUIRED");
  const keyType = input.type ?? "other";
  if (keyType !== "key" && keyType !== "card" && keyType !== "other") {
    throw new EquipmentApiError("EQUIPMENT_INVALID_TYPE");
  }
  validateProtocolPhotos(input.photos ?? []);
  const notes = validateConditionNotes(input.conditionNotes);

  const protocol = toEquipmentProtocol(
    await rpcEquipment(
      EQUIPMENT_RPC.initiateHandover,
      {
        p_org_id: input.orgId,
        p_worker_id: input.workerId,
        p_kind: "key_card",
        p_key_name: name,
        p_key_type: keyType,
        p_condition_notes: notes,
      },
      "initiateKeyCardHandover",
    ),
  );
  return attachEvidence(protocol, input.photos, notes);
}

export async function initiateAssetReturn(input: {
  asset: Pick<EquipmentAsset, "id" | "status">;
  conditionNotes?: string | null;
  photos?: File[];
}): Promise<EquipmentProtocol> {
  if (!canReturnAsset(input.asset)) throw new EquipmentApiError("EQUIPMENT_INVALID_STATE");
  validateProtocolPhotos(input.photos ?? []);
  const notes = validateConditionNotes(input.conditionNotes);
  const protocol = toEquipmentProtocol(
    await rpcEquipment(
      EQUIPMENT_RPC.initiateReturn,
      {
        p_kind: "asset",
        p_asset_id: input.asset.id,
        p_condition_notes: notes,
      },
      "initiateAssetReturn",
    ),
  );
  return attachEvidence(protocol, input.photos, notes);
}

export async function initiateKeyCardReturn(input: {
  item: Pick<StaffEquipmentItem, "id" | "status">;
  conditionNotes?: string | null;
  photos?: File[];
}): Promise<EquipmentProtocol> {
  if (!canReturnStaffItem(input.item)) throw new EquipmentApiError("EQUIPMENT_INVALID_STATE");
  validateProtocolPhotos(input.photos ?? []);
  const notes = validateConditionNotes(input.conditionNotes);
  const protocol = toEquipmentProtocol(
    await rpcEquipment(
      EQUIPMENT_RPC.initiateReturn,
      {
        p_kind: "key_card",
        p_staff_equipment_id: input.item.id,
        p_condition_notes: notes,
      },
      "initiateKeyCardReturn",
    ),
  );
  return attachEvidence(protocol, input.photos, notes);
}

export async function respondEquipmentProtocol(input: {
  protocolId: string;
  accept: boolean;
  rejectionReason?: string | null;
}): Promise<EquipmentProtocol> {
  if (!input.protocolId) throw new EquipmentApiError("EQUIPMENT_NOT_FOUND");
  const rejectionReason = input.accept ? null : validateConditionNotes(input.rejectionReason);
  if (!input.accept && !rejectionReason) {
    throw new EquipmentApiError("EQUIPMENT_REJECTION_REASON_REQUIRED");
  }
  return toEquipmentProtocol(
    await rpcEquipment(
      EQUIPMENT_RPC.respond,
      {
        p_protocol_id: input.protocolId,
        p_accept: input.accept,
        p_rejection_reason: rejectionReason,
      },
      "respondEquipmentProtocol",
    ),
  );
}

export async function cancelEquipmentProtocol(protocolId: string): Promise<EquipmentProtocol> {
  if (!protocolId) throw new EquipmentApiError("EQUIPMENT_NOT_FOUND");
  return toEquipmentProtocol(
    await rpcEquipment(
      EQUIPMENT_RPC.cancel,
      { p_protocol_id: protocolId },
      "cancelEquipmentProtocol",
    ),
  );
}

export async function retireEquipmentAsset(assetId: string): Promise<EquipmentAsset> {
  if (!assetId) throw new EquipmentApiError("EQUIPMENT_NOT_FOUND");
  return toEquipmentAsset(
    await rpcEquipment(
      EQUIPMENT_RPC.retireAsset,
      { p_asset_id: assetId },
      "retireEquipmentAsset",
    ),
  );
}
